import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  fetchRepairRequestCapacityDays,
  fetchRepairRequestDropoffAvailability,
  fetchRepairRequestForm,
  formatFirebaseError,
  submitRepairRequest,
  warmup,
  type RepairRequestFormData,
} from "../api";
import type { RepairCapacityDay } from "../types";
import { applySalonBranding } from "../branding";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { RepairDayCalendar, formatDateKey } from "../components/RepairDayCalendar";
import { SlotPicker } from "../components/SlotPicker";
import { detectLocale, t, type Locale } from "../i18n";
import { groupServiceProducts, STANDARDS_CATEGORY } from "../serviceProductGroups";
import { LegalFooter } from "../LegalFooter";
import { resizeImageFileForUpload } from "../imageUtils";
import { formatPrice, formatSlotDate, formatSlotTime } from "../slots";

type StepId =
  | "object"
  | "services"
  | "repairDay"
  | "details"
  | "contact"
  | "dropoff"
  | "review"
  | "success";

type PhotoDraft = {
  previewUrl: string;
  dataBase64: string;
  mimeType: string;
  fileName: string;
};

type Draft = {
  fields: Record<string, string>;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  photos: PhotoDraft[];
  dropoffEmployeeId: string | null;
  dropoffStartAt: string | null;
  serviceProductIds: string[];
  repairDay: string | null;
  dropoffChoice: string | null;
  /** Honeypot — leave empty. */
  website: string;
  acceptConsent: boolean;
};

const emptyDraft = (): Draft => ({
  fields: {},
  description: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  street: "",
  postalCode: "",
  city: "",
  photos: [],
  dropoffEmployeeId: null,
  dropoffStartAt: null,
  serviceProductIds: [],
  repairDay: null,
  dropoffChoice: null,
  website: "",
  acceptConsent: false,
});

const PHONE_RE = /^\+?[\d\s\-().]{6,}$/;

function fieldLabel(
  field: { key: string; label: Record<string, string> | string },
  locale: Locale
): string {
  if (typeof field.label === "string") return field.label || field.key;
  return (
    field.label[locale] ||
    field.label.de ||
    field.label.en ||
    Object.values(field.label)[0] ||
    field.key
  );
}

function renderLabelTemplate(
  template: string,
  fields: Record<string, string>,
  fallback: string
): string {
  let result = template;
  for (const [key, value] of Object.entries(fields)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, "g"), value);
  }
  result = result.replace(/\{[^}]+\}/g, "").replace(/\s+/g, " ").trim();
  return result || fallback;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function fill(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce(
    (s, [key, value]) => s.replaceAll(`{${key}}`, value),
    template
  );
}

function clientRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `rr_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function stepLabel(locale: Locale, id: StepId): string {
  switch (id) {
    case "object":
      return t(locale, "rrStepObject");
    case "services":
      return t(locale, "rrStepServices");
    case "repairDay":
      return t(locale, "rrStepRepairDay");
    case "details":
      return t(locale, "rrStepDetails");
    case "contact":
      return t(locale, "rrStepContact");
    case "dropoff":
      return t(locale, "rrStepDropoff");
    case "review":
      return t(locale, "rrStepReview");
    case "success":
      return "";
  }
}

export function RepairRequestPage() {
  const { code: salonCode = "" } = useParams();
  const [locale] = useState<Locale>(detectLocale());
  const [data, setData] = useState<RepairRequestFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<StepId>("object");
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    orderNumber: string;
    claimCode: string | null;
    repairDropoff?: { date: string; from: string; to: string } | null;
  } | null>(null);
  const [capacityDays, setCapacityDays] = useState<RepairCapacityDay[]>([]);
  const [requestId] = useState(clientRequestId);

  useEffect(() => {
    applySalonBranding(data?.branding ?? null);
  }, [data?.branding]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const form = await fetchRepairRequestForm(salonCode);
        if (!cancelled) setData(form);
      } catch (err) {
        if (!cancelled) {
          const msg = formatFirebaseError(err);
          setError(
            msg.includes("disabled") || msg.includes("paid") || msg.includes("not-found")
              ? t(locale, "rrUnavailable")
              : t(locale, "errorGeneric")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salonCode, locale]);

  const templateFields = useMemo(
    () => data?.objectTemplate?.fields ?? [],
    [data]
  );

  const objectLabel = useMemo(() => {
    if (!data?.objectTemplate) {
      return draft.description.trim().slice(0, 80);
    }
    return renderLabelTemplate(
      data.objectTemplate.labelTemplate,
      draft.fields,
      data.objectTemplate.name
    );
  }, [data, draft.fields, draft.description]);

  const canContinueObject = useMemo(() => {
    for (const field of templateFields) {
      if (field.required && !String(draft.fields[field.key] ?? "").trim()) {
        return false;
      }
    }
    return templateFields.length === 0 || Object.values(draft.fields).some((v) => v.trim());
  }, [templateFields, draft.fields]);

  const requestMode =
    data?.requestMode ??
    (data?.dropoff.enabled ? "generalDropoff" : "general");
  const hasServices =
    requestMode === "services" ||
    requestMode === "servicesDropoff" ||
    requestMode === "servicesCapacity";
  const hasCapacity = requestMode === "servicesCapacity";
  const serviceProductGroups = useMemo(
    () =>
      groupServiceProducts(
        data?.serviceProducts ?? [],
        data?.serviceProductCategoryOrder ?? []
      ),
    [data?.serviceProducts, data?.serviceProductCategoryOrder]
  );
  const hasDropoffStep =
    requestMode === "generalDropoff" || requestMode === "servicesDropoff";
  const steps = useMemo<StepId[]>(() => {
    const next: StepId[] = ["object"];
    if (hasServices) next.push("services");
    if (hasCapacity) next.push("repairDay");
    next.push("details", "contact");
    if (hasDropoffStep) next.push("dropoff");
    next.push("review");
    return next;
  }, [hasServices, hasCapacity, hasDropoffStep]);

  const canContinueContact =
    draft.firstName.trim().length > 0 &&
    draft.lastName.trim().length > 0 &&
    isValidEmail(draft.email.trim()) &&
    PHONE_RE.test(draft.phone.trim());

  useEffect(() => {
    if (!data) return;
    if (hasCapacity) warmup("getRepairRequestCapacityDays");
    if (hasDropoffStep) warmup("getRepairRequestDropoffAvailability");
    if (step !== "object" && step !== "services" && step !== "success") {
      warmup("submitRepairRequest");
    }
  }, [data, hasCapacity, hasDropoffStep, step]);

  useEffect(() => {
    if (!hasCapacity || !salonCode || draft.serviceProductIds.length === 0) {
      setCapacityDays([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchRepairRequestCapacityDays(
          salonCode,
          draft.serviceProductIds
        );
        if (!cancelled) setCapacityDays(res.days);
      } catch {
        if (!cancelled) setCapacityDays([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasCapacity, salonCode, draft.serviceProductIds]);

  async function handleAddPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    const remaining = 4 - draft.photos.length;
    if (remaining <= 0) return;
    const selected = Array.from(files).slice(0, remaining);
    const resized = await Promise.all(
      selected.map((file) =>
        resizeImageFileForUpload(file).then(
          (payload): PhotoDraft => ({ previewUrl: URL.createObjectURL(file), ...payload }),
          () => null // skip bad files
        )
      )
    );
    const next = resized.filter((p): p is PhotoDraft => p != null);
    setDraft((d) => ({ ...d, photos: [...d.photos, ...next] }));
  }

  function removePhoto(index: number) {
    setDraft((d) => {
      const photos = [...d.photos];
      const [removed] = photos.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return { ...d, photos };
    });
  }

  function goTo(next: StepId) {
    setError(null);
    setStep(next);
  }

  function goBack() {
    const idx = steps.indexOf(step as Exclude<StepId, "success">);
    if (idx > 0) goTo(steps[idx - 1]);
  }

  function goNextFrom(current: StepId) {
    const idx = steps.indexOf(current);
    if (idx >= 0 && idx < steps.length - 1) goTo(steps[idx + 1]);
  }

  async function handleSubmit() {
    if (!data || submitting || !canContinueContact) return;
    if (hasServices && draft.serviceProductIds.length === 0) {
      setError(t(locale, "rrPickServicesHint"));
      setStep("services");
      return;
    }
    if (hasCapacity && (!draft.repairDay || !draft.dropoffChoice)) {
      setError(t(locale, "rrPickRepairDay"));
      setStep("repairDay");
      return;
    }
    if (hasDropoffStep && data.dropoff.required && !draft.dropoffStartAt) {
      setError(t(locale, "rrDropoffRequired"));
      setStep("dropoff");
      return;
    }
    if (!draft.acceptConsent) {
      setError(t(locale, "consentRequired"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const address =
        draft.street.trim() || draft.postalCode.trim() || draft.city.trim()
          ? {
              street: draft.street.trim(),
              postalCode: draft.postalCode.trim(),
              city: draft.city.trim(),
            }
          : null;
      const res = await submitRepairRequest({
        salonCode,
        description: draft.description.trim(),
        contact: {
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          email: draft.email.trim(),
          phone: draft.phone.trim(),
          address,
        },
        templateId: data.objectTemplate?.id ?? null,
        dropoff:
          draft.dropoffEmployeeId && draft.dropoffStartAt
            ? {
                employeeId: draft.dropoffEmployeeId,
                startAt: draft.dropoffStartAt,
              }
            : undefined,
        serviceProductIds: draft.serviceProductIds,
        repairDay: draft.repairDay ?? undefined,
        dropoffChoice: draft.dropoffChoice ?? undefined,
        assetRef: {
          type: data.objectTemplate?.assetType ?? "other",
          label: objectLabel || data.objectTemplate?.assetType || "Anfrage",
          fields: draft.fields,
        },
        photos: draft.photos.map((p) => ({
          dataBase64: p.dataBase64,
          mimeType: p.mimeType,
          fileName: p.fileName,
        })),
        clientRequestId: requestId,
        website: draft.website,
        locale,
      });
      setResult({
        orderNumber: res.orderNumber,
        claimCode: res.claimCode,
        repairDropoff: res.repairDropoff,
      });
      setStep("success");
    } catch (err) {
      setError(formatFirebaseError(err) || t(locale, "errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <p className="muted">{t(locale, "loading")}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page">
        <div className="card">
          <h2>{t(locale, "rrTitle")}</h2>
          <p>{error ?? t(locale, "rrUnavailable")}</p>
        </div>
      </div>
    );
  }

  const stepLabels = steps.map((id) => stepLabel(locale, id));
  const currentIndex = steps.indexOf(step as Exclude<StepId, "success">);

  function renderServiceGroups() {
    return (
      <div className="rr-service-categories">
        {serviceProductGroups.map((group) => {
          const open =
            serviceProductGroups.length === 1 ||
            group.category === STANDARDS_CATEGORY ||
            !group.category;
          return (
            <details
              key={group.category || "uncategorized"}
              className="rr-category"
              open={open}
            >
              <summary>
                {group.category ? group.category : t(locale, "rrUncategorized")}
              </summary>
              <div className="check-list">
                {group.items.map((product) => {
                  const checked = draft.serviceProductIds.includes(product.id);
                  const price = data?.showPricesToCustomers
                    ? formatPrice(product.priceCents, data.currency ?? "EUR", locale)
                    : "";
                  const meta = [
                    product.durationMinutes > 0 ? `${product.durationMinutes} min` : "",
                    price,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <label
                      key={product.id}
                      className={`check-card${checked ? " selected" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setDraft((d) => {
                            const ids = checked
                              ? d.serviceProductIds.filter((id) => id !== product.id)
                              : [...d.serviceProductIds, product.id];
                            return {
                              ...d,
                              serviceProductIds: ids,
                              repairDay: null,
                              dropoffChoice: null,
                            };
                          })
                        }
                      />
                      <span className="check-card-body">
                        <span className="option-title">{product.name}</span>
                        {meta && (
                          <span className="option-sub check-card-meta">{meta}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>
    );
  }

  return (
    <div className="page">
      <header className="header">
        {data.branding.logoUrl ? (
          <img className="logo" src={data.branding.logoUrl} alt={data.salonName} />
        ) : (
          <div className="logo" />
        )}
        <div>
          <h1 className="salon-name">{data.salonName}</h1>
          <p className="salon-meta">{t(locale, "rrTitle")}</p>
        </div>
      </header>

      {data.intro && step !== "success" && <p className="muted intro">{data.intro}</p>}

      {step !== "success" && (
        <div className="steps">
          {stepLabels.map((label, index) => (
            <span
              key={`${steps[index]}-${label}`}
              className={`step-pill ${index === currentIndex ? "active" : ""} ${
                index < currentIndex ? "done" : ""
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      <div className="card">
        {step === "object" && (
          <>
            <h2>{t(locale, "rrObjectHeading")}</h2>
            <div className="form-grid">
              {templateFields.map((field) => (
                <label key={field.key}>
                  {fieldLabel(field, locale)}
                  {field.required ? " *" : ""}
                  <input
                    value={draft.fields[field.key] ?? ""}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        fields: { ...d.fields, [field.key]: e.target.value },
                      }))
                    }
                  />
                </label>
              ))}
              {objectLabel && (
                <p className="muted">
                  {t(locale, "rrObjectPreview")}: <strong>{objectLabel}</strong>
                </p>
              )}
            </div>
            <div className="actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canContinueObject}
                onClick={() => goNextFrom("object")}
              >
                {t(locale, "continue")}
              </button>
            </div>
          </>
        )}

        {step === "services" && (
          <>
            <h2>{t(locale, "rrPickServices")}</h2>
            <p className="muted photo-hint">{t(locale, "rrPickServicesHint")}</p>
            {data.showPricesToCustomers && (
              <p className="muted photo-hint">{t(locale, "rrPricesExcludeParts")}</p>
            )}
            {renderServiceGroups()}
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={draft.serviceProductIds.length === 0}
                onClick={() => goNextFrom("services")}
              >
                {t(locale, "continue")}
              </button>
            </div>
          </>
        )}

        {step === "repairDay" && (
          <>
            <h2>{t(locale, "rrPickRepairDay")}</h2>
            {capacityDays.filter((d) => d.available).length === 0 ? (
              <p>{t(locale, "rrNoCapacityDays")}</p>
            ) : (
              <RepairDayCalendar
                locale={locale}
                days={capacityDays}
                selectedDate={draft.repairDay}
                dropoffChoice={draft.dropoffChoice}
                onSelectDate={(dateKey) => {
                  const day = capacityDays.find((d) => d.date === dateKey);
                  setDraft((d) => ({
                    ...d,
                    repairDay: dateKey,
                    dropoffChoice: day?.dropoffOptions[0]?.choice ?? null,
                  }));
                }}
                onSelectDropoff={(choice) =>
                  setDraft((d) => ({ ...d, dropoffChoice: choice }))
                }
              />
            )}
            {error && <p className="error">{error}</p>}
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!draft.repairDay || !draft.dropoffChoice}
                onClick={() => goNextFrom("repairDay")}
              >
                {t(locale, "continue")}
              </button>
            </div>
          </>
        )}

        {step === "details" && (
          <>
            <h2>{t(locale, "rrDetailsHeading")}</h2>
            <div className="form-grid">
              <label>
                {t(locale, "rrDescription")}
                <textarea
                  rows={5}
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  placeholder={t(locale, "rrDescriptionHint")}
                />
              </label>
              <div>
                <span className="photo-section-label">{t(locale, "rrPhotos")}</span>
                <p className="muted photo-hint">{t(locale, "rrPhotosHint")}</p>
                <div className="photo-grid">
                  {draft.photos.map((photo, index) => (
                    <div key={photo.fileName + index} className="photo-thumb">
                      <img src={photo.previewUrl} alt="" />
                      <button
                        type="button"
                        className="chip photo-remove"
                        onClick={() => removePhoto(index)}
                      >
                        {t(locale, "rrRemovePhoto")}
                      </button>
                    </div>
                  ))}
                </div>
                {draft.photos.length < 4 && (
                  <label className="btn btn-secondary file-btn">
                    {t(locale, "rrAddPhoto")}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(e) => {
                        void handleAddPhotos(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => goNextFrom("details")}
              >
                {t(locale, "continue")}
              </button>
            </div>
          </>
        )}

        {step === "contact" && (
          <>
            <h2>{t(locale, "rrContactHeading")}</h2>
            <div className="form-grid">
              <label>
                {t(locale, "rrFirstName")} *
                <input
                  value={draft.firstName}
                  onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
                  autoComplete="given-name"
                />
              </label>
              <label>
                {t(locale, "rrLastName")} *
                <input
                  value={draft.lastName}
                  onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
                  autoComplete="family-name"
                />
              </label>
              <label>
                {t(locale, "rrPhoneRequired")} *
                <input
                  type="tel"
                  value={draft.phone}
                  onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                  autoComplete="tel"
                />
              </label>
              {draft.phone.trim() && !PHONE_RE.test(draft.phone.trim()) && (
                <p className="error">{t(locale, "rrInvalidPhone")}</p>
              )}
              <label>
                {t(locale, "rrEmailRequired")} *
                <input
                  type="email"
                  value={draft.email}
                  onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                  autoComplete="email"
                />
              </label>
              <p className="muted">{t(locale, "rrAddressOptional")}</p>
              <AddressAutocomplete
                locale={locale}
                onResolved={(address) =>
                  setDraft((d) => ({
                    ...d,
                    street: address.street,
                    postalCode: address.postalCode,
                    city: address.city,
                  }))
                }
              />
              <label>
                {t(locale, "rrStreet")}
                <input
                  value={draft.street}
                  onChange={(e) => setDraft((d) => ({ ...d, street: e.target.value }))}
                  autoComplete="street-address"
                />
              </label>
              <label>
                {t(locale, "rrPostalCode")}
                <input
                  value={draft.postalCode}
                  onChange={(e) => setDraft((d) => ({ ...d, postalCode: e.target.value }))}
                  autoComplete="postal-code"
                />
              </label>
              <label>
                {t(locale, "rrCity")}
                <input
                  value={draft.city}
                  onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
                  autoComplete="address-level2"
                />
              </label>
              <label className="hp" aria-hidden="true">
                Website
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  value={draft.website}
                  onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
                />
              </label>
            </div>
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canContinueContact}
                onClick={() => goNextFrom("contact")}
              >
                {t(locale, "continue")}
              </button>
            </div>
          </>
        )}

        {step === "dropoff" && (
          <>
            <h2>{t(locale, "rrDropoffHeading")}</h2>
            <p className="muted">{t(locale, "rrDropoffHint")}</p>
            <SlotPicker
              locale={locale}
              employeeId={draft.dropoffEmployeeId}
              horizonDays={data.dropoff.horizonDays}
              loadSlots={(dateKey) =>
                fetchRepairRequestDropoffAvailability(
                  salonCode,
                  dateKey,
                  draft.dropoffEmployeeId ?? undefined
                )
              }
              selectedStartAt={draft.dropoffStartAt}
              onEmployeeId={(employeeId) =>
                setDraft((d) =>
                  d.dropoffEmployeeId === employeeId
                    ? d
                    : { ...d, dropoffEmployeeId: employeeId }
                )
              }
              onSelect={(slot) =>
                setDraft((d) => ({ ...d, dropoffStartAt: slot.startAt }))
              }
            />
            {draft.dropoffStartAt && (
              <div className="summary" style={{ marginTop: 20 }}>
                <strong>{t(locale, "rrDropoffSelected")}</strong>
                <p>{formatSlotDate(new Date(draft.dropoffStartAt), locale)}</p>
                <p>{formatSlotTime(new Date(draft.dropoffStartAt), locale)}</p>
              </div>
            )}
            {error && <p className="error">{error}</p>}
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                {t(locale, "back")}
              </button>
              {!data.dropoff.required && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setDraft((d) => ({ ...d, dropoffStartAt: null }));
                    goTo("review");
                  }}
                >
                  {t(locale, "rrDropoffSkip")}
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={!draft.dropoffStartAt}
                onClick={() => goTo("review")}
              >
                {t(locale, "continue")}
              </button>
            </div>
          </>
        )}

        {step === "review" && (
          <>
            <h2>{t(locale, "rrReviewHeading")}</h2>
            <div className="summary">
              <p>
                <strong>{objectLabel || "—"}</strong>
              </p>
              {draft.description.trim() && <p>{draft.description}</p>}
              <p className="muted">
                {[draft.firstName, draft.lastName].filter(Boolean).join(" ")}
                {" · "}
                {draft.email}
                {" · "}
                {draft.phone}
              </p>
              {(draft.street || draft.postalCode || draft.city) && (
                <p className="muted">
                  {[draft.street, [draft.postalCode, draft.city].filter(Boolean).join(" ")]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              )}
              {draft.photos.length > 0 && (
                <p className="muted">
                  {t(locale, "rrPhotos")}: {draft.photos.length}
                </p>
              )}
              {draft.serviceProductIds.length > 0 && (
                <p className="muted">
                  {t(locale, "rrSelectedServices")}:{" "}
                  {draft.serviceProductIds
                    .map(
                      (id) =>
                        (data.serviceProducts ?? []).find((p) => p.id === id)?.name ?? id
                    )
                    .join(", ")}
                </p>
              )}
              {draft.repairDay && (
                <p className="muted">
                  {t(locale, "rrPickRepairDay")}: {formatDateKey(draft.repairDay, locale)}
                  {(() => {
                    const opt = capacityDays
                      .find((d) => d.date === draft.repairDay)
                      ?.dropoffOptions.find((o) => o.choice === draft.dropoffChoice);
                    if (!opt) return "";
                    return ` · ${fill(
                      t(
                        locale,
                        opt.choice === "dayBefore"
                          ? "rrDropoffDayBeforeChoice"
                          : "rrDropoffSameDayChoice"
                      ),
                      {
                        date: formatDateKey(opt.date, locale),
                        from: opt.from,
                        to: opt.to,
                      }
                    )}`;
                  })()}
                </p>
              )}
              {draft.dropoffStartAt && (
                <p className="muted">
                  {t(locale, "rrDropoffSelected")}:{" "}
                  {formatSlotDate(new Date(draft.dropoffStartAt), locale)},{" "}
                  {formatSlotTime(new Date(draft.dropoffStartAt), locale)}
                </p>
              )}
            </div>
            <label className="checkbox-row" style={{ marginTop: 16 }}>
              <input
                type="checkbox"
                checked={draft.acceptConsent}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, acceptConsent: e.target.checked }))
                }
                required
              />
              <span>
                {t(locale, "consentPrefix")}
                <a
                  href={`https://werkstate.app/privacy.html?lang=${locale}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(locale, "consentPrivacyLink")}
                </a>
                {t(locale, "consentAnd")}
                <a
                  href={`https://werkstate.app/terms.html?lang=${locale}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(locale, "consentTermsLink")}
                </a>
              </span>
            </label>
            {error && <p className="error">{error}</p>}
            <div className="actions">
              <button type="button" className="btn btn-secondary" onClick={goBack}>
                {t(locale, "back")}
              </button>
              <button
                type="button"
                className={`btn btn-primary${submitting ? " loading" : ""}`}
                disabled={submitting || !draft.acceptConsent}
                onClick={() => void handleSubmit()}
              >
                {submitting ? t(locale, "rrSubmitting") : t(locale, "rrSubmit")}
              </button>
            </div>
          </>
        )}

        {step === "success" && result && (
          <>
            <div className="success-icon">✓</div>
            <h2>{t(locale, "rrSuccessTitle")}</h2>
            <p>{t(locale, "rrSuccessBody")}</p>
            {result.repairDropoff && (
              <div className="summary" style={{ marginTop: 16, marginBottom: 16 }}>
                <h3 style={{ margin: "0 0 8px" }}>{t(locale, "rrDropoffHintTitle")}</h3>
                <p>
                  {fill(t(locale, "rrDropoffHintBody"), {
                    date: formatDateKey(result.repairDropoff.date, locale),
                    from: result.repairDropoff.from,
                    to: result.repairDropoff.to,
                  })}
                </p>
              </div>
            )}
            <p className="muted">{t(locale, "rrSuccessEmail")}</p>
            <p>
              <strong>
                {t(locale, "rrOrderNumber")}: {result.orderNumber}
              </strong>
            </p>
            <div className="summary" style={{ marginTop: 24 }}>
              <h3 style={{ margin: "0 0 8px" }}>{t(locale, "rrDownloadApp")}</h3>
              <p className="muted">{t(locale, "rrDownloadAppHint")}</p>
              <a
                className="btn btn-primary"
                href="https://werkstate.app/download.html"
                style={{ display: "inline-block", textDecoration: "none", marginTop: 12 }}
              >
                {t(locale, "rrDownloadApp")}
              </a>
            </div>
          </>
        )}
      </div>

      <p className="footer">
        {t(locale, "rrPoweredBy")} WERKSTATE
      </p>
      <LegalFooter locale={locale} />

      <style>{`
        .intro { margin: 0 0 1rem; }
        .photo-section-label {
          display: block;
          font-family: "Space Mono", ui-monospace, monospace;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .photo-hint { margin: 0 0 12px; }
        .photo-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 12px;
        }
        .photo-thumb {
          width: 96px;
          background: var(--card);
          border: 1.5px solid var(--border);
          border-radius: 10px;
          box-shadow: 2px 2px 0 var(--border);
          overflow: hidden;
          padding-bottom: 6px;
        }
        .photo-thumb img {
          width: 100%;
          height: 72px;
          object-fit: cover;
          display: block;
          border-bottom: 1.5px solid var(--border-soft);
        }
        .photo-remove {
          display: block;
          margin: 6px auto 0;
          padding: 4px 10px;
          font-size: 0.7rem;
        }
        .file-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .hp {
          position: absolute;
          left: -9999px;
          height: 0;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
